import React, { useState, useEffect } from 'react';
import { 
  Star, Users, Building, Globe, Heart, Award, Target, Send, 
  Plus, Search, Filter, MessageCircle, Calendar, Clock, CheckCircle,
  User, Mail, Phone, MapPin, ExternalLink, Download, Upload,
  Handshake, Lightbulb, TrendingUp, Shield, Zap, Eye
} from 'lucide-react';

const MentorPartnerProgram = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mentorApplications, setMentorApplications] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [activeMentors, setActiveMentors] = useState([]);
  const [activePartners, setActivePartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(null);

  // Mock data for mentors
  const mockMentors = [
    {
      id: 1,
      name: 'Dr. Sarah Martinez',
      title: 'Sustainability Leadership Expert',
      company: 'EcoLeadership Institute',
      location: 'San Francisco, CA',
      specializations: ['Conscious Leadership', 'ESG Strategy', 'Carbon Management'],
      rating: 4.9,
      sessionsCompleted: 156,
      experience: '15+ years',
      languages: ['English', 'Spanish'],
      availability: 'Available',
      profileImage: null,
      bio: 'Former Chief Sustainability Officer with extensive experience helping organizations transition to conscious business practices.',
      achievements: ['Certified CNESS Mentor', 'GRI Standards Expert', 'B-Corp Consultant'],
      hourlyRate: '$200/hour',
      responseTime: 'Within 24 hours'
    },
    {
      id: 2,
      name: 'Michael Chen',
      title: 'Circular Economy Specialist',
      company: 'CircularTech Solutions',
      location: 'Seattle, WA',
      specializations: ['Circular Economy', 'Waste Reduction', 'Supply Chain Sustainability'],
      rating: 4.8,
      sessionsCompleted: 89,
      experience: '12+ years',
      languages: ['English', 'Mandarin'],
      availability: 'Limited',
      profileImage: null,
      bio: 'Helps organizations implement circular economy principles and reduce environmental impact.',
      achievements: ['Circular Economy Certified', 'Supply Chain Expert', 'Zero Waste Consultant'],
      hourlyRate: '$175/hour',
      responseTime: 'Within 48 hours'
    }
  ];

  // Mock data for partners
  const mockPartners = [
    {
      id: 1,
      name: 'Sustainable Solutions Inc.',
      type: 'Technology Partner',
      industry: 'CleanTech',
      size: '500-1000 employees',
      location: 'Austin, TX',
      specializations: ['Carbon Tracking', 'Energy Management', 'Sustainability Analytics'],
      certificationLevel: 'Luminary',
      partnershipSince: '2023',
      projects: 45,
      clientRating: 4.7,
      services: ['Technology Integration', 'Consulting', 'Training'],
      contactPerson: 'Jennifer Walsh',
      email: 'partnerships@sustainablesolutions.com',
      phone: '+1 (512) 555-0123',
      website: 'https://sustainablesolutions.com'
    },
    {
      id: 2,
      name: 'GreenImpact Consulting',
      type: 'Service Partner',
      industry: 'Consulting',
      size: '50-100 employees',
      location: 'Portland, OR',
      specializations: ['Impact Assessment', 'Community Engagement', 'Stakeholder Management'],
      certificationLevel: 'Inspired',
      partnershipSince: '2024',
      projects: 23,
      clientRating: 4.9,
      services: ['Impact Assessment', 'Community Programs', 'Stakeholder Engagement'],
      contactPerson: 'David Rodriguez',
      email: 'hello@greenimpact.org',
      phone: '+1 (503) 555-0145',
      website: 'https://greenimpact.org'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    // Simulate API calls
    setTimeout(() => {
      setActiveMentors(mockMentors);
      setActivePartners(mockPartners);
      setMentorApplications([
        {
          id: 1,
          name: 'Emma Thompson',
          email: 'emma@example.com',
          specializations: ['Community Engagement', 'Stakeholder Relations'],
          status: 'pending',
          submittedDate: '2024-10-01'
        }
      ]);
      setPartnerApplications([
        {
          id: 1,
          organizationName: 'EcoTech Innovations',
          contactName: 'Alex Johnson',
          email: 'alex@ecotech.com',
          partnershipType: 'Technology',
          status: 'under_review',
          submittedDate: '2024-09-28'
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const handleApplicationSubmit = (type, data) => {
    if (type === 'mentor') {
      setMentorApplications(prev => [...prev, { ...data, id: Date.now(), status: 'pending', submittedDate: new Date().toISOString().split('T')[0] }]);
    } else {
      setPartnerApplications(prev => [...prev, { ...data, id: Date.now(), status: 'pending', submittedDate: new Date().toISOString().split('T')[0] }]);
    }
    setShowApplicationModal(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mentor & Partner Program</h1>
        <p className="text-gray-600">Connect with certified mentors and trusted partners to accelerate your conscious business journey</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 mb-6">
        <div className="flex space-x-8">
          {[
            { id: 'overview', label: 'Program Overview' },
            { id: 'mentors', label: 'Find Mentors' },
            { id: 'partners', label: 'Partner Directory' },
            { id: 'applications', label: 'My Applications' },
            { id: 'apply', label: 'Apply to Join' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Program Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Mentor Program with Background Image */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Star className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Mentor Program</h3>
                  <p className="text-green-600 font-medium">Get Expert Guidance</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <p className="text-gray-700">1-on-1 sessions with certified experts</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <p className="text-gray-700">Customized guidance for your industry</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <p className="text-gray-700">Access to exclusive resources and tools</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <p className="text-gray-700">Flexible scheduling and multiple languages</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{activeMentors.length}+</p>
                  <p className="text-sm text-gray-600">Certified Mentors</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">500+</p>
                  <p className="text-sm text-gray-600">Sessions Completed</p>
                </div>
              </div>

              <button 
                onClick={() => setActiveTab('mentors')}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Find a Mentor
              </button>
            </div>

            {/* Partner Program */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Handshake className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Partner Network</h3>
                  <p className="text-blue-600 font-medium">Collaborate & Grow</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <p className="text-gray-700">Certified service and technology partners</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <p className="text-gray-700">Joint initiatives and co-marketing opportunities</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <p className="text-gray-700">Access to specialized tools and resources</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <p className="text-gray-700">Regional and global partnership opportunities</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{activePartners.length}+</p>
                  <p className="text-sm text-gray-600">Active Partners</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">100+</p>
                  <p className="text-sm text-gray-600">Joint Projects</p>
                </div>
              </div>

              <button 
                onClick={() => setActiveTab('partners')}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Explore Partners
              </button>
            </div>
          </div>

          {/* Success Stories */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Success Stories</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Building className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">TechForGood Inc.</p>
                    <p className="text-sm text-gray-600">Technology Startup</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "Working with our CNESS mentor helped us increase our CIS score by 40% in just 6 months. The guidance was invaluable for our sustainability transformation."
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>🎯 40% CIS improvement</span>
                  <span>⏱️ 6 months</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Globe className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Global Manufacturing Co.</p>
                    <p className="text-sm text-gray-600">Manufacturing</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "Our partnership with SustainableTech enabled us to reduce carbon emissions by 35% while improving operational efficiency."
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>🌍 35% emissions reduction</span>
                  <span>📈 Efficiency gains</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mentors Tab */}
      {activeTab === 'mentors' && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="relative flex-1">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by specialization, industry, or location..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                <option>All Specializations</option>
                <option>Conscious Leadership</option>
                <option>ESG Strategy</option>
                <option>Carbon Management</option>
                <option>Circular Economy</option>
                <option>Community Engagement</option>
              </select>
            </div>
          </div>

          {/* Mentors Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activeMentors.map(mentor => (
              <div key={mentor.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">{mentor.name}</h4>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        <span className="text-sm font-medium text-gray-700">{mentor.rating}</span>
                      </div>
                    </div>
                    <p className="text-green-600 font-medium">{mentor.title}</p>
                    <p className="text-sm text-gray-600">{mentor.company}</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-4 text-sm">{mentor.bio}</p>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{mentor.location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{mentor.experience} experience</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">Responds {mentor.responseTime}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Specializations:</p>
                  <div className="flex flex-wrap gap-2">
                    {mentor.specializations.map((spec, index) => (
                      <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-lg font-bold text-gray-900">{mentor.hourlyRate}</p>
                    <p className="text-xs text-gray-500">{mentor.sessionsCompleted} sessions completed</p>
                  </div>
                  <div className="flex space-x-2">
                    <button className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 text-sm">
                      View Profile
                    </button>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                      Book Session
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <div className="space-y-6">
          {/* Partner Categories */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { type: 'Technology', icon: Zap, count: 15, color: 'blue' },
              { type: 'Service', icon: Star, count: 12, color: 'green' },
              { type: 'Regional', icon: Globe, count: 8, color: 'purple' },
              { type: 'Certification', icon: Shield, count: 5, color: 'orange' }
            ].map(category => {
              const Icon = category.icon;
              return (
                <div key={category.type} className={`bg-${category.color}-50 border border-${category.color}-200 rounded-lg p-4 text-center`}>
                  <div className={`w-12 h-12 bg-${category.color}-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                    <Icon className={`h-6 w-6 text-${category.color}-600`} />
                  </div>
                  <p className="font-medium text-gray-900">{category.type} Partners</p>
                  <p className="text-2xl font-bold text-gray-700">{category.count}</p>
                </div>
              );
            })}
          </div>

          {/* Partners List */}
          <div className="grid grid-cols-1 gap-6">
            {activePartners.map(partner => (
              <div key={partner.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{partner.name}</h4>
                      <div className="flex items-center space-x-3 text-sm text-gray-600 mt-1">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{partner.type}</span>
                        <span>•</span>
                        <span>{partner.industry}</span>
                        <span>•</span>
                        <span>{partner.size}</span>
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-current" />
                          <span className="text-sm font-medium text-gray-700">{partner.clientRating}</span>
                        </div>
                        <span className="text-sm text-gray-600">{partner.projects} projects completed</span>
                        <span className="text-sm text-gray-600">Partner since {partner.partnershipSince}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    partner.certificationLevel === 'Luminary' ? 'bg-gold-100 text-gold-800' : 'bg-purple-100 text-purple-800'
                  }`}>
                    {partner.certificationLevel}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Specializations:</p>
                    <div className="flex flex-wrap gap-2">
                      {partner.specializations.map((spec, index) => (
                        <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Services:</p>
                    <div className="flex flex-wrap gap-2">
                      {partner.services.map((service, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Mail className="h-4 w-4" />
                      <span>{partner.email}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Phone className="h-4 w-4" />
                      <span>{partner.phone}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <ExternalLink className="h-4 w-4" />
                      <a href={partner.website} className="text-blue-600 hover:underline">Website</a>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm">
                      View Details
                    </button>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                      Connect
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-6">
          {/* My Applications Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Mentor Applications</h3>
              {mentorApplications.length === 0 ? (
                <p className="text-gray-500 mb-4">No mentor applications submitted yet.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {mentorApplications.map(app => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Mentor Application</p>
                        <p className="text-sm text-gray-600">Submitted: {app.submittedDate}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <button 
                onClick={() => setShowApplicationModal('mentor')}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Apply to be a Mentor
              </button>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Applications</h3>
              {partnerApplications.length === 0 ? (
                <p className="text-gray-500 mb-4">No partnership applications submitted yet.</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {partnerApplications.map(app => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{app.organizationName}</p>
                        <p className="text-sm text-gray-600">Submitted: {app.submittedDate}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        app.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                        app.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {app.status.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <button 
                onClick={() => setShowApplicationModal('partner')}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply for Partnership
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Tab */}
      {activeTab === 'apply' && (
        <div className="space-y-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Join Our Network</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Become part of our growing community of conscious business leaders. Choose how you'd like to contribute to the movement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Become a Mentor */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Become a Mentor</h3>
                <p className="text-gray-600">Share your expertise and help others on their conscious business journey</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>5+ years of relevant experience required</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>CNESS certification or equivalent credentials</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Flexible scheduling options available</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Competitive compensation and recognition</span>
                </div>
              </div>

              <button 
                onClick={() => setShowApplicationModal('mentor')}
                className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Apply to be a Mentor
              </button>
            </div>

            {/* Become a Partner */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Handshake className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Become a Partner</h3>
                <p className="text-gray-600">Join our partner network and collaborate on impactful projects</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>Established organization with proven track record</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>Alignment with conscious business values</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>Access to exclusive partner benefits</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>Co-marketing and collaboration opportunities</span>
                </div>
              </div>

              <button 
                onClick={() => setShowApplicationModal('partner')}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Apply for Partnership
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Modals */}
      {showApplicationModal && (
        <ApplicationModal
          type={showApplicationModal}
          onClose={() => setShowApplicationModal(null)}
          onSubmit={handleApplicationSubmit}
        />
      )}
    </div>
  );
};

// Application Modal Component
const ApplicationModal = ({ type, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    title: '',
    experience: '',
    specializations: [],
    motivation: '',
    availability: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(type, formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {type === 'mentor' ? 'Apply to be a Mentor' : 'Apply for Partnership'}
          </h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'mentor' ? 'Current Organization' : 'Organization Name'}
              </label>
              <input
                type="text"
                value={formData.organization}
                onChange={(e) => setFormData({...formData, organization: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {type === 'mentor' ? 'Why do you want to become a mentor?' : 'Why do you want to partner with us?'}
            </label>
            <textarea
              value={formData.motivation}
              onChange={(e) => setFormData({...formData, motivation: e.target.value})}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              required
            />
          </div>
        </form>
        
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 text-white rounded-lg hover:opacity-90 ${
              type === 'mentor' ? 'bg-green-600' : 'bg-blue-600'
            }`}
          >
            Submit Application
          </button>
        </div>
      </div>
    </div>
  );
};

export default MentorPartnerProgram;